import { useState } from "react";
import { useMapProjects } from "../../../hooks/useDashboardQueries";
import { MapProject } from "../../../types/dashboard.types";
import Emitter from "../../../utils/emitter";
import Map from "../../Overview/Map";
import { cardStatus } from "../cardStatus";
import DashboardCard from "../DashboardCard";
import { BADGES } from "../dashboardConfig";
import { TenantWidgetProps } from "../tenantChoice";
import { useGuardedNavigate, useTenantChoice } from "../widgetHooks";
import WidgetSelect from "../WidgetSelect";

const TITLE = "Project map";

type ChipKey = "inProgress" | "new" | "completed";

/** Dark count text reads on all three squares (white fails on green and blue). */
const CHIP_TEXT_COLOR = "#141414";

const CHIPS: Array<{
	key: ChipKey;
	label: string;
	status: string;
	color: string;
}> = [
	{
		key: "inProgress",
		label: "In progress",
		status: "In progress",
		color: "#1b8fb3",
	},
	{ key: "new", label: "New", status: "New", color: "#f0f0f0" },
	{
		key: "completed",
		label: "Completed",
		status: "Resolved",
		color: "#6fd04a",
	},
];

// Counting and pins follow the project map page (Overview): closed projects
// count as completed and only show as pins when that chip is on.
const isCompleted = (project: MapProject) =>
	!!project.closed || project.status === "Resolved";

const inChip = (project: MapProject, chip: ChipKey) => {
	if (chip === "completed") return isCompleted(project);
	const status = CHIPS.find((item) => item.key === chip)?.status;
	return !project.closed && project.status === status;
};

/** The selected tenant's projects on the existing map, filterable by status. */
function ProjectMapWidget({ tenants, currentTenantKey }: TenantWidgetProps) {
	const { go } = useGuardedNavigate();
	const { choice, tenant, setTenant, target, label } = useTenantChoice(
		tenants,
		"none",
		currentTenantKey
	);
	const [chip, setChip] = useState<ChipKey | null>(null);
	const tenantKey = target?.tenantKey ?? null;
	const query = useMapProjects(tenantKey);
	const projects = query.data ?? [];

	const options = choice.options.map((option) =>
		option.value === tenantKey && query.data
			? { ...option, note: `(${query.data.length} projects)` }
			: option
	);
	const pins = chip
		? projects.filter((project) => inChip(project, chip))
		: projects.filter((project) => !project.closed);

	const openProject = (project: MapProject) => {
		if (tenantKey === currentTenantKey) {
			go(`/projects/${project.project_code}`, { state: { ...project } }, () => {
				Emitter.emit("projectName", project.project_name);
				localStorage.setItem("currentProject", project.project_code);
			});
		} else {
			// The project route offers to switch to the project's tenant.
			go(`/projects/${project.project_code}?tenant_key=${tenantKey}`);
		}
	};

	return (
		<DashboardCard
			badge={BADGES.projects}
			title={TITLE}
			className="dashboard-widget--map dashboard-widget--tall"
			flush
			controls={
				<WidgetSelect
					value={tenant}
					options={options}
					onChange={(value) => {
						setTenant(value);
						setChip(null);
					}}
					ariaLabel={`Tenant for ${TITLE}`}
					noteInLabel
				/>
			}
			onExpand={() => go(`/dashboard/project-map?tenant_key=${tenantKey}`)}
			status={cardStatus(query, (data) => data.length === 0)}
			emptyText="No projects to show"
			forbiddenText={`You don't have access to projects for ${label}.`}
			onRetry={query.refetch}
		>
			<div className="dashboard-map">
				<Map
					projectList={pins}
					keyType="project_code"
					selectProject={(_code: string, project: MapProject) =>
						openProject(project)
					}
					selectedProject={null}
					selectedProjectInfo={null}
					// The dashboard scrolls; the wheel must scroll it, not zoom the map.
					scrollWheelZoom={false}
				/>
				<div
					className="dashboard-map__chips"
					role="group"
					aria-label="Filter the map by project status"
				>
					{CHIPS.map((item) => (
						<button
							key={item.key}
							type="button"
							className="dashboard-map__chip"
							aria-pressed={chip === item.key}
							onClick={() =>
								setChip((current) => (current === item.key ? null : item.key))
							}
						>
							<span
								className="dashboard-map__chip-count"
								style={{ background: item.color, color: CHIP_TEXT_COLOR }}
							>
								{projects.filter((project) => inChip(project, item.key)).length}
							</span>
							{item.label}
						</button>
					))}
				</div>
			</div>
		</DashboardCard>
	);
}

export default ProjectMapWidget;
